import type {
    AddressingCountryCode,
    AddressingCurrencyCode,
    AddressingNumericCountryCode,
    AddressingThreeLetterCountryCode,
    AddressingLocaleKey,
    AddressingCountryMetadata,
} from '../generated/types';

import { toAddressingLocaleKey } from './locales';

import {
    loadAddressingCountryMetadata,
    countryNamesLoaders,
} from '../generated/loaders';
import { isAddressingCountryCode } from './enum-typeguards';
import { createAddressFormat, type AddressFormat } from './address-formats';

export type AddressingCountryWithoutName = {
    __typename: 'Country';
    code: AddressingCountryCode;
    currencyCode: AddressingCurrencyCode | null;
    numericCode: AddressingNumericCountryCode | null;
    threeLetterCode: AddressingThreeLetterCountryCode | null;
    addressFormat: AddressFormat;
    supportedByAddress: true;
};

export type AddressingCountry = AddressingCountryWithoutName & {
    name: string;
    locale: AddressingLocaleKey;
};

export type AddressingCountryMapWithoutNames = ReadonlyMap<
    AddressingCountryCode,
    AddressingCountryWithoutName
>;

export type AddressingCountryMap = ReadonlyMap<
    AddressingCountryCode,
    AddressingCountry
>;

const addressingCountryMapWithoutNameCacheKey = Symbol(
    'addressingCountryMapWithoutNameCacheKey'
);

const addressingCountriesCache: Partial<
    Record<AddressingLocaleKey, AddressingCountryMap>
> & {
    [addressingCountryMapWithoutNameCacheKey]?: AddressingCountryMapWithoutNames;
} = {};

export function getAddressingCountryMapWithoutNames(): Promise<AddressingCountryMapWithoutNames>;
export function getAddressingCountryMapWithoutNames(
    callback: (result: AddressingCountryMapWithoutNames) => void,
    onError?: (reason: unknown) => void
): void;
export function getAddressingCountryMapWithoutNames(
    callback?: (result: AddressingCountryMapWithoutNames) => void,
    onError?: (reason: unknown) => void
) {
    if (!callback) {
        return new Promise(getAddressingCountryMapWithoutNames);
    }

    if (addressingCountriesCache[addressingCountryMapWithoutNameCacheKey]) {
        callback(
            addressingCountriesCache[addressingCountryMapWithoutNameCacheKey]
        );
    }

    loadAddressingCountryMetadata().then((countriesMetadata) => {
        try {
            const result = new Map(
                Object.entries(countriesMetadata).map(([k, v]) => [
                    k,
                    toAddressingCountryWithoutName(v),
                ])
            ) as AddressingCountryMapWithoutNames;

            addressingCountriesCache[addressingCountryMapWithoutNameCacheKey] =
                result;

            callback(result);
        } catch (e) {
            onError?.(e);
        }
    }, onError);
}

export type GetAddressingCountryOptions = {
    locale?: string | null | undefined;
    fallbackLocale?: AddressingLocaleKey;
};

export async function getAddressingCountryMap({
    locale,
    fallbackLocale,
}: GetAddressingCountryOptions = {}): Promise<AddressingCountryMap> {
    const localeKey = toAddressingLocaleKey(locale, fallbackLocale);

    if (addressingCountriesCache[localeKey]) {
        return addressingCountriesCache[localeKey];
    }

    const [countryMapWithoutName, countryNames] = await Promise.all([
        getAddressingCountryMapWithoutNames(),
        countryNamesLoaders[localeKey],
    ]);

    const result = new Map(
        Object.entries(countryNames)
            .map(([k, name]) => {
                const countryWithoutName = countryMapWithoutName.get(
                    k as AddressingCountryCode
                );
                if (!countryWithoutName) return undefined;

                return [
                    k,
                    {
                        ...countryWithoutName,
                        name,
                        locale,
                    },
                ];
            })
            .filter((v): v is [AddressingCountryCode, AddressingCountry] => !!v)
    ) as AddressingCountryMap;

    addressingCountriesCache[localeKey] = result;
    return result;
}

function toAddressingCountryWithoutName(
    metadata: AddressingCountryMetadata
): AddressingCountryWithoutName {
    return {
        __typename: 'Country',
        code: metadata.countryCode,
        currencyCode: metadata.currencyCode,
        numericCode: metadata.numericCode,
        threeLetterCode: metadata.threeLetterCode,
        addressFormat: createAddressFormat(metadata.addressFormat),
        supportedByAddress: true,
    };
}

export function toAddressingCountryCode(
    countryCode: string | null | undefined
): AddressingCountryCode | undefined {
    if (!countryCode) return undefined;
    const candidate = countryCode.toUpperCase().substring(0, 2);
    if (isAddressingCountryCode(candidate)) {
        return candidate;
    }
    return undefined;
}

export async function getAddressingCountry(
    countryCode: AddressingCountryCode | string,
    options?: GetAddressingCountryOptions
): Promise<AddressingCountry | undefined> {
    const map = await getAddressingCountryMap(options);
    const _countryCode = toAddressingCountryCode(countryCode);
    if (!_countryCode) return undefined;
    return map.get(_countryCode);
}
